import React, { FC } from 'react';

import { Descendant, Element } from 'slate';

import DeeplinkEditor from '../../../editors/PushNotificationEditor/Editor/components/DeeplinkComponent';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

// helpers
import getCapitalizedText from '../../../helpers/stringHelpers';

// models
import {
    CMSPlaceholderElement,
    DatasetElement,
    ElementKind,
    TextElement,
    VariantElement,
} from '../../../models/editor.model';
import { FormulaDetailModel } from '../../../models/formula.model';

// components
import FormulaVariantChip from '../chips/FormulaVariantChip';

interface PnPreviewCardProps {
    formula: FormulaDetailModel;
    formulaStructure?: Descendant[];
}

const getDisplayText = (item: Element | TextElement) => {
    if ((item as DatasetElement).datasetId) {
        return 'Dataset';
    }
    if ((item as VariantElement).variantType) {
        return (item as Element)?.children?.[0].text;
    }
    if ((item as CMSPlaceholderElement).cmsPlaceholderId) {
        return 'Cart reference';
    }
    if ((item as Element).kind === ElementKind.buttonText) {
        return `Text: ${(item as Element)?.children?.[0].text}`;
    }
    if ((item as Element).kind === ElementKind.buttonAction) {
        return `Action: ${(item as Element)?.children?.[0].text}`;
    }

    return (
        (item as TextElement)?.text ||
        (item as Element)?.children?.[0].text ||
        ''
    );
};

const titles = (
    item: any,
): { text: string; variantType: string; id?: string }[] => {
    return item?.children
        ?.map((child: any) => ({
            text: getDisplayText(child),
            variantType: child?.variantType || item?.variantType,
            id: child?.id || item?.id,
        }))
        .filter((child: any) => child?.text);
};

const getHeading = (content: Element): string => {
    const elementKind = content.kind;
    if (elementKind === ElementKind.section) {
        return content.sectionType;
    } else if (elementKind === ElementKind.image) {
        return content.kind;
    } else if (elementKind === ElementKind.button) {
        return `${content.buttonType} Button`;
    }
    return '';
};

const PnPreviewCard: FC<React.PropsWithChildren<PnPreviewCardProps>> = ({
    formulaStructure,
    formula,
}) => {
    return (
        <Container>
            {!formulaStructure ? (
                <NoMessageText>{'None composed'}</NoMessageText>
            ) : (
                formulaStructure.map((content, index) => {
                    const messages = titles(content);
                    return (
                        <div key={index}>
                            <Heading>
                                {getCapitalizedText(
                                    getHeading(content as Element),
                                )}
                            </Heading>
                            <Content>
                                {messages?.length ? (
                                    messages.map((title, index) => (
                                        <FormulaVariantChip
                                            key={index}
                                            title={title?.text}
                                            variantType={title?.variantType}
                                        />
                                    ))
                                ) : (
                                    <NoMessageText>
                                        {'None composed'}
                                    </NoMessageText>
                                )}
                            </Content>
                        </div>
                    );
                })
            )}

            <StyledDeeplinkEditor formula={formula} isDisabled />
        </Container>
    );
};

const Container = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(8),
    color: aampeTheme.colors.blue900,
}));

const NoMessageText = styled.span(() => ({
    color: aampeTheme.colors.solidA01,
}));

const Heading = styled.span(() => ({
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.blue900,
}));

const Content = styled.div(() => ({
    marginTop: aampeTheme.spacing(1),
    width: 'fit-content',
    display: 'flex',
    flexWrap: 'wrap',
    gap: aampeTheme.spacing4(2),
    alignItems: 'flex-end',
}));

const StyledDeeplinkEditor = styled(DeeplinkEditor)({
    '& > p': {
        fontWeight: aampeTheme.weights.light,
    },
    minHeight: '150px',
});

export default PnPreviewCard;
